import { Select } from "@bafe/ui";
import { ChevronDown } from "reicon-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useSession } from "../../lib/session.js";
import { HubCreatableFieldSelect } from "./hub-creatable-field-select.js";
import {
	HubOperationBar,
	HubOperationEmpty,
	HubOperationFilterMenu,
	HubOperationModal,
	HubOperationScreen,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationToolbar,
	HubOperationColumnMenu,
	HubOperationFilterClear,
	HubOperationListTable,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	hubOperationGroupedHasRows,
} from "./hub-operation-shell.js";
import { useHubOperationPagination } from "./hub-operation-pagination.js";
import { HubInventoryLookupFieldSelect } from "./hub-inventory-lookup-field-select.js";
import type { Books, InventoryHistoryEntry, InventoryRow, LookupRow } from "./hub-operation-types.js";
import {
	customerLabel,
	customersOf,
	formatMyr,
	INVENTORY_CUSTODY_TAGS,
	INVENTORY_STATUSES,
	inventoryRow,
	nextInventoryAstId,
	siteRow,
	sitesForCustomer,
	textOf,
	useHubSave,
	vendorLabel,
	vendorsOf,
} from "./hub-operation-shared.js";
import {
	INVENTORY_TABLE_COLUMNS,
	inventoryRowMatchesSearch,
	mergeInventoryColumnVisibility,
	type InventoryColumnId,
} from "./hub-operation-inventory-table.js";

export type InventoryBooks = Books & {
	inventory: InventoryRow[];
	inventoryCategories: LookupRow[];
	inventoryBrands: LookupRow[];
};

type InventoryGroupBy = "category" | "status" | "custody" | "brand" | "";
function inventoryGroupKey(row: InventoryRow, books: InventoryBooks, groupBy: InventoryGroupBy): string {
	switch (groupBy) {
		case "status":
			return INVENTORY_STATUSES.find(([id]) => id === row.status)?.[1] ?? row.status;
		case "custody":
			return custodySummary(books, row);
		case "brand":
			return row.brand.trim() || "—";
		case "category":
		default:
			return row.category.trim() || "Uncategorized";
	}
}

function InventoryFormField({
	label,
	htmlFor,
	children,
}: {
	label: string;
	htmlFor: string;
	children: ReactNode;
}) {
	return (
		<div className="hub-inventory-form-field flex min-w-0 flex-col gap-1.5">
			<label htmlFor={htmlFor}>{label}</label>
			{children}
		</div>
	);
}

function statusPill(status: InventoryRow["status"]): string {
	if (status === "active") return "ok";
	if (status === "loose") return "warn";
	return "idle";
}

function custodyPill(tag: InventoryRow["custodyTag"]): string {
	return tag === "external" ? "info" : "idle";
}

function mergeLookupNames(rows: LookupRow[], fromRows: string[]): string[] {
	const set = new Set<string>();
	for (const row of rows) {
		const name = row.name.trim();
		if (name) set.add(name);
	}
	for (const name of fromRows) {
		const trimmed = name.trim();
		if (trimmed) set.add(trimmed);
	}
	return [...set].sort((a, b) => a.localeCompare(b));
}

async function ensureLookup(list: "inventoryCategories" | "inventoryBrands", name: string, books: InventoryBooks) {
	const trimmed = name.trim();
	if (!trimmed) return;
	const rows = list === "inventoryCategories" ? books.inventoryCategories : books.inventoryBrands;
	if (rows.some((row) => row.name.toLowerCase() === trimmed.toLowerCase())) return;
	await api.appendHubBook({ list, record: { name: trimmed } });
}

function inventoryFromForm(
	fields: FormData,
	astId: string,
	history: InventoryHistoryEntry[],
): InventoryRow {
	const custodyTag = textOf(fields, "custodyTag") === "external" ? "external" : "internal";
	return {
		astId,
		status: (textOf(fields, "status") || "active") as InventoryRow["status"],
		category: textOf(fields, "category") || "Uncategorized",
		description: textOf(fields, "description"),
		brand: textOf(fields, "brand"),
		model: textOf(fields, "model"),
		serialNumber: textOf(fields, "serialNumber"),
		it: textOf(fields, "it"),
		purchaseDate: textOf(fields, "purchaseDate"),
		purchasePrice: textOf(fields, "purchasePrice"),
		ourWarrantyExpiry: textOf(fields, "ourWarrantyExpiry"),
		supplierWarrantyExpiry: textOf(fields, "supplierWarrantyExpiry"),
		vendorId: textOf(fields, "vendorId"),
		custodyTag,
		customerId: custodyTag === "external" ? textOf(fields, "customerId") : "",
		siteId: custodyTag === "external" ? textOf(fields, "siteId") : "",
		history,
	};
}

function TransferLocationFields({
	books,
	prefix,
	row,
}: {
	books: InventoryBooks;
	prefix: string;
	row: InventoryRow;
}) {
	const [custodyTag, setCustodyTag] = useState<InventoryRow["custodyTag"]>(row.custodyTag);
	const [customerId, setCustomerId] = useState(row.customerId);
	const siteOptions = useMemo(() => sitesForCustomer(books, customerId), [books, customerId]);
	return (
		<div key={row.id ?? row.astId}>
			<label htmlFor={`${prefix}-custody`}>Custody</label>
			<Select
				id={`${prefix}-custody`}
				name="custodyTag"
				value={custodyTag}
				onChange={(event) => setCustodyTag(event.target.value === "external" ? "external" : "internal")}
			>
				{INVENTORY_CUSTODY_TAGS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
			</Select>
			{custodyTag === "external" ? (
				<div className="grid two">
					<div>
						<label htmlFor={`${prefix}-customer`}>Customer</label>
						<Select
							id={`${prefix}-customer`}
							name="customerId"
							value={customerId}
							onChange={(event) => setCustomerId(event.target.value)}
							required
						>
							<option value="">Choose customer</option>
							{customersOf(books).map((item) => (
								<option key={item.id ?? item.name} value={String(item.id ?? "")}>{item.name}</option>
							))}
						</Select>
					</div>
					<div>
						<label htmlFor={`${prefix}-site`}>Site</label>
						<Select
							key={`${prefix}-site-${customerId}`}
							id={`${prefix}-site`}
							name="siteId"
							defaultValue={row.siteId}
							required
							disabled={!customerId}
						>
							<option value="">{customerId ? "Choose site" : "Choose customer first"}</option>
							{siteOptions.map((item) => (
								<option key={item.id ?? item.name} value={String(item.id ?? "")}>{item.name}</option>
							))}
						</Select>
					</div>
				</div>
			) : null}
		</div>
	);
}

function InventoryFormFields({
	books,
	prefix,
	editing,
	onRefresh,
}: {
	books: InventoryBooks;
	prefix: string;
	editing?: InventoryRow | null;
	onRefresh: () => Promise<void>;
}) {
	const categories = mergeLookupNames(
		books.inventoryCategories,
		books.inventory.map((row) => row.category),
	);
	const brands = mergeLookupNames(
		books.inventoryBrands,
		books.inventory.map((row) => row.brand),
	);
	const vendors = vendorsOf(books);
	const [custodyTag, setCustodyTag] = useState<InventoryRow["custodyTag"]>(editing?.custodyTag ?? "internal");
	const [customerId, setCustomerId] = useState(editing?.customerId ?? "");
	const siteOptions = useMemo(() => sitesForCustomer(books, customerId), [books, customerId]);

	return (
		<div key={editing?.id ?? "new"} className="hub-inventory-asset-form flex flex-col gap-4">
			{editing?.astId ? (
				<div className="flex flex-col gap-1.5">
					<span className="quiet text-[13px] font-medium">Asset ID</span>
					<p className="m-0">{editing.astId}</p>
				</div>
			) : null}
			<HubCreatableFieldSelect
				id={`${prefix}-vendor`}
				name="vendorId"
				label="Vendor"
				defaultValue={editing?.vendorId ?? ""}
				items={[["", "No vendor"], ...vendors.map((row) => [String(row.id ?? ""), row.name] as [string, string])]}
				allowCreate={false}
				menuInModal
			/>
			<HubInventoryLookupFieldSelect
				id={`${prefix}-category`}
				name="category"
				label="Category"
				defaultValue={editing?.category ?? ""}
				items={categories}
				list="inventoryCategories"
				lookupRows={books.inventoryCategories}
				inventory={books.inventory}
				onRefresh={onRefresh}
			/>
			<InventoryFormField label="Description" htmlFor={`${prefix}-description`}>
				<input id={`${prefix}-description`} name="description" defaultValue={editing?.description ?? ""} required />
			</InventoryFormField>
			<div className="grid two hub-inventory-form-row">
				<HubInventoryLookupFieldSelect
					id={`${prefix}-brand`}
					name="brand"
					label="Brand"
					defaultValue={editing?.brand ?? ""}
					items={brands}
					list="inventoryBrands"
					lookupRows={books.inventoryBrands}
					inventory={books.inventory}
					onRefresh={onRefresh}
				/>
				<InventoryFormField label="Model" htmlFor={`${prefix}-model`}>
					<input id={`${prefix}-model`} name="model" defaultValue={editing?.model ?? ""} />
				</InventoryFormField>
			</div>
			<InventoryFormField label="Serial number" htmlFor={`${prefix}-serial`}>
				<input id={`${prefix}-serial`} name="serialNumber" defaultValue={editing?.serialNumber ?? ""} />
			</InventoryFormField>
			<InventoryFormField label="IT" htmlFor={`${prefix}-it`}>
				<textarea id={`${prefix}-it`} name="it" rows={3} defaultValue={editing?.it ?? ""} />
			</InventoryFormField>
			<div className="grid two hub-inventory-form-row">
				<InventoryFormField label="Status" htmlFor={`${prefix}-status`}>
					<Select id={`${prefix}-status`} name="status" defaultValue={editing?.status ?? "active"}>
						{INVENTORY_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
					</Select>
				</InventoryFormField>
				<InventoryFormField label="Custody" htmlFor={`${prefix}-custody`}>
					<Select
						id={`${prefix}-custody`}
						name="custodyTag"
						value={custodyTag}
						onChange={(event) => setCustodyTag(event.target.value === "external" ? "external" : "internal")}
					>
						{INVENTORY_CUSTODY_TAGS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
					</Select>
				</InventoryFormField>
			</div>
			{custodyTag === "external" ? (
				<div className="grid two hub-inventory-form-row">
					<InventoryFormField label="Customer" htmlFor={`${prefix}-customer`}>
						<Select
							id={`${prefix}-customer`}
							name="customerId"
							value={customerId}
							onChange={(event) => setCustomerId(event.target.value)}
							required
						>
							<option value="">Choose customer</option>
							{customersOf(books).map((row) => (
								<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
							))}
						</Select>
					</InventoryFormField>
					<InventoryFormField label="Site" htmlFor={`${prefix}-site`}>
						<Select
							key={`${prefix}-site-${customerId}`}
							id={`${prefix}-site`}
							name="siteId"
							defaultValue={editing?.siteId ?? ""}
							required
							disabled={!customerId}
						>
							<option value="">{customerId ? "Choose site" : "Choose customer first"}</option>
							{siteOptions.map((row) => (
								<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
							))}
						</Select>
					</InventoryFormField>
				</div>
			) : null}
			<div className="grid two hub-inventory-form-row">
				<InventoryFormField label="Purchase date" htmlFor={`${prefix}-purchase-date`}>
					<input id={`${prefix}-purchase-date`} name="purchaseDate" type="date" defaultValue={editing?.purchaseDate ?? ""} />
				</InventoryFormField>
				<InventoryFormField label="Purchase price (MYR)" htmlFor={`${prefix}-purchase-price`}>
					<input id={`${prefix}-purchase-price`} name="purchasePrice" type="text" inputMode="decimal" placeholder="0.00" defaultValue={editing?.purchasePrice ?? ""} />
				</InventoryFormField>
			</div>
			<div className="grid two hub-inventory-form-row">
				<InventoryFormField label="Our warranty expiry" htmlFor={`${prefix}-our-warranty`}>
					<input id={`${prefix}-our-warranty`} name="ourWarrantyExpiry" type="date" defaultValue={editing?.ourWarrantyExpiry ?? ""} />
				</InventoryFormField>
				<InventoryFormField label="Supplier warranty expiry" htmlFor={`${prefix}-supplier-warranty`}>
					<input id={`${prefix}-supplier-warranty`} name="supplierWarrantyExpiry" type="date" defaultValue={editing?.supplierWarrantyExpiry ?? ""} />
				</InventoryFormField>
			</div>
		</div>
	);
}

function custodySummary(books: InventoryBooks, row: InventoryRow): string {
	if (row.custodyTag === "external") {
		const customer = customerLabel(books, row.customerId);
		const site = siteRow(books, row.siteId)?.name ?? "";
		return [customer, site].filter(Boolean).join(" · ") || "With Client";
	}
	return "On Hands";
}

function inventoryCell(
	books: InventoryBooks,
	row: InventoryRow,
	columnId: InventoryColumnId,
): ReactNode {
	switch (columnId) {
		case "astId":
			return row.id ? <Link className="row-link" to={`/hub/inventory/${row.id}`}>{row.astId}</Link> : row.astId;
		case "status":
			return (
				<span className={`doc-pill ${statusPill(row.status)}`}>
					{INVENTORY_STATUSES.find(([v]) => v === row.status)?.[1] ?? row.status}
				</span>
			);
		case "category":
			return row.category || "—";
		case "description":
			return row.description || "—";
		case "brand":
			return row.brand || "—";
		case "model":
			return row.model || "—";
		case "serialNumber":
			return row.serialNumber || "—";
		case "purchaseDate":
			return row.purchaseDate || "—";
		case "purchasePrice":
			return formatMyr(row.purchasePrice);
		case "ourWarrantyExpiry":
			return row.ourWarrantyExpiry || "—";
		case "supplierWarrantyExpiry":
			return row.supplierWarrantyExpiry || "—";
		case "vendorId":
			return vendorLabel(books, row.vendorId) || "—";
		case "custody":
			return <span className={`doc-pill ${custodyPill(row.custodyTag)}`}>{custodySummary(books, row)}</span>;
		default:
			return "—";
	}
}

function InventoryGroupedTable({
	books,
	groupedRows,
	collapsedGroups,
	onToggleGroup,
	showGroupHeaders,
	sortKey,
	sortDir,
	onSort,
	columnVisibility,
	onRemove,
	emptyMessage,
}: {
	books: InventoryBooks;
	groupedRows: Array<[string, InventoryRow[]]>;
	collapsedGroups: string[];
	onToggleGroup: (group: string) => void;
	showGroupHeaders: boolean;
	sortKey: string;
	sortDir: "asc" | "desc";
	onSort: (key: string) => void;
	columnVisibility: Record<InventoryColumnId, boolean>;
	onRemove: (id: number) => void;
	emptyMessage: string;
}) {
	const visibleColumns = INVENTORY_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);
	const colSpan = visibleColumns.length + 1;
	const hasRows = hubOperationGroupedHasRows(groupedRows);

	return (
		<div className="table-wrap hub-inventory-table-wrap">
			<table className="hub-inventory-table">
				<thead>
					<tr>
						{visibleColumns.map((column) => (
							<th key={column.id}>
								{column.sortable ? (
									<button type="button" className="th-sort plain" onClick={() => onSort(column.id)}>
										{column.label}
										{sortKey === column.id ? (sortDir === "asc" ? " ↑" : " ↓") : null}
									</button>
								) : column.label}
							</th>
						))}
						<th className="tight"></th>
					</tr>
				</thead>
				<tbody>
					{!hasRows ? <HubOperationTableEmptyRow colSpan={colSpan}>{emptyMessage}</HubOperationTableEmptyRow> : null}
					{hasRows ? groupedRows.flatMap(([groupLabel, groupItems]) => {
						const collapsed = collapsedGroups.includes(groupLabel);
						const header = showGroupHeaders ? (
							<tr key={`grp-${groupLabel}`} className="task-group-row hub-inventory-group-row">
								<th scope="rowgroup" colSpan={colSpan}>
									<button
										type="button"
										className={`hub-inventory-group-toggle${collapsed ? " is-collapsed" : ""}`}
										aria-expanded={!collapsed}
										onClick={() => onToggleGroup(groupLabel)}
									>
										<ChevronDown size={14} className="task-table-group-chevron" aria-hidden="true" />
										<span className="task-group-head">
											<span className="task-group-name">{groupLabel}</span>
										</span>
									</button>
								</th>
							</tr>
						) : null;
						return [
							...(header ? [header] : []),
							...(showGroupHeaders && collapsed
								? []
								: groupItems.map((row) => (
									<tr key={row.id ?? row.astId}>
										{visibleColumns.map((column) => (
											<td key={column.id} data-label={column.label}>
												{inventoryCell(books, row, column.id)}
											</td>
										))}
										<td className="tight" onClick={(event) => event.stopPropagation()}>
											{row.id ? (
												<details className="select menu">
													<summary aria-label="Actions">
														<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg>
													</summary>
													<div className="select-pop">
														<Link className="select-item" to={`/hub/inventory/${row.id}`}>View</Link>
														<button className="select-item" type="button" onClick={() => onRemove(row.id!)}>Delete</button>
													</div>
												</details>
											) : null}
										</td>
									</tr>
								))),
						];
					}) : null}
				</tbody>
			</table>
		</div>
	);
}

function InventoryDetailView({
	books,
	detail,
	onRemove,
}: {
	books: InventoryBooks;
	detail: InventoryRow;
	onRemove: (id: number) => void;
}) {
	const client = useQueryClient();
	const session = useSession();
	const refreshBooks = async () => {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	};
	const actor = session.data?.user.fullName || session.data?.user.email || "Someone";
	const [editing, setEditing] = useState<InventoryRow | null>(null);
	const [transferOpen, setTransferOpen] = useState(false);

	function pushHistory(
		base: InventoryHistoryEntry[],
		entry: Omit<InventoryHistoryEntry, "at" | "by">,
	): InventoryHistoryEntry[] {
		return [{ ...entry, at: new Date().toISOString(), by: actor }, ...base].slice(0, 50);
	}

	const form = useHubSave(async (fields) => {
		if (!detail.id) return;
		const category = textOf(fields, "category");
		const brand = textOf(fields, "brand");
		await Promise.all([
			ensureLookup("inventoryCategories", category, books),
			brand ? ensureLookup("inventoryBrands", brand, books) : Promise.resolve(),
		]);
		const prior = detail;
		const next = inventoryFromForm(fields, prior.astId, prior.history ?? []);
		const history = [...(prior.history ?? [])];
		if (prior.status !== next.status) {
			history.unshift({
				at: new Date().toISOString(),
				by: actor,
				action: "status",
				detail: `Status changed from ${prior.status} to ${next.status}.`,
				from: prior.status,
				to: next.status,
			});
		}
		if (prior.custodyTag !== next.custodyTag || prior.customerId !== next.customerId || prior.siteId !== next.siteId) {
			history.unshift({
				at: new Date().toISOString(),
				by: actor,
				action: "custody",
				detail: `Custody updated to ${next.custodyTag === "external" ? custodySummary(books, next) : "On Hands"}.`,
				from: prior.custodyTag,
				to: next.custodyTag,
			});
		}
		await api.updateHubBook({ list: "inventory", id: detail.id, record: { ...next, history: history.slice(0, 50) } });
		setEditing(null);
	});

	const transferForm = useHubSave(async (fields) => {
		if (!detail.id) return;
		const custodyTag = textOf(fields, "custodyTag") === "external" ? "external" : "internal";
		const next: InventoryRow = {
			...detail,
			custodyTag,
			customerId: custodyTag === "external" ? textOf(fields, "customerId") : "",
			siteId: custodyTag === "external" ? textOf(fields, "siteId") : "",
			history: pushHistory(detail.history ?? [], {
				action: "custody",
				detail: `Transferred to ${custodyTag === "external" ? "With Client" : "On Hands"}.`,
				from: detail.custodyTag,
				to: custodyTag,
			}),
		};
		await api.updateHubBook({ list: "inventory", id: detail.id, record: next });
		setTransferOpen(false);
	});

	return (
		<div>
			<HubOperationBar sub={`${detail.astId} · ${detail.description || "Asset"}`}>
				<Link className="btn ghost" to="/hub/inventory">All inventory</Link>
				<button className="btn ghost" type="button" onClick={() => { setEditing(detail); form.setOpen(true); }}>Edit</button>
				<button className="btn ghost" type="button" onClick={() => setTransferOpen(true)}>Transfer</button>
				{detail.id ? (
					<button className="btn ghost" type="button" onClick={() => void onRemove(detail.id!)}>Delete</button>
				) : null}
			</HubOperationBar>
			<div className="panel grid">
				<div className="grid two">
					<div><span className="quiet">Status</span><p><span className={`doc-pill ${statusPill(detail.status)}`}>{INVENTORY_STATUSES.find(([v]) => v === detail.status)?.[1] ?? detail.status}</span></p></div>
					<div><span className="quiet">Custody</span><p><span className={`doc-pill ${custodyPill(detail.custodyTag)}`}>{custodySummary(books, detail)}</span></p></div>
					<div><span className="quiet">Category</span><p>{detail.category || "—"}</p></div>
					<div><span className="quiet">Brand</span><p>{detail.brand || "—"}</p></div>
					<div><span className="quiet">Model</span><p>{detail.model || "—"}</p></div>
					<div><span className="quiet">Serial number</span><p>{detail.serialNumber || "—"}</p></div>
					<div><span className="quiet">IT</span><p>{detail.it || "—"}</p></div>
					<div><span className="quiet">Purchase date</span><p>{detail.purchaseDate || "—"}</p></div>
					<div><span className="quiet">Purchase price</span><p>{formatMyr(detail.purchasePrice)}</p></div>
					<div><span className="quiet">Our warranty expiry</span><p>{detail.ourWarrantyExpiry || "—"}</p></div>
					<div><span className="quiet">Supplier warranty expiry</span><p>{detail.supplierWarrantyExpiry || "—"}</p></div>
					<div><span className="quiet">Vendor</span><p>{vendorLabel(books, detail.vendorId) || "—"}</p></div>
				</div>
			</div>
			<h2>History</h2>
			{(detail.history ?? []).length === 0 ? <HubOperationEmpty>No history yet.</HubOperationEmpty> : (
				<div className="table-wrap">
					<table>
						<thead><tr><th>When</th><th>Action</th><th>Detail</th><th>By</th></tr></thead>
						<tbody>
							{(detail.history ?? []).map((entry, index) => (
								<tr key={`${entry.at}-${index}`}>
									<td data-label="When">{new Date(entry.at).toLocaleString()}</td>
									<td data-label="Action">{entry.action}</td>
									<td data-label="Detail">{entry.detail}</td>
									<td data-label="By">{entry.by || "—"}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
			<HubOperationModal hubForm open={form.open} title="Edit asset" error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<InventoryFormFields books={books} prefix="inv-edit" editing={editing} onRefresh={refreshBooks} />
			</HubOperationModal>
			<HubOperationModal open={transferOpen} title="Transfer asset" error={transferForm.error} pending={transferForm.pending} onClose={() => setTransferOpen(false)} onSubmit={(fields) => void transferForm.save(fields)}>
				<TransferLocationFields books={books} prefix="inv-transfer" row={detail} />
			</HubOperationModal>
		</div>
	);
}

function asInventoryBooks(books: Books): InventoryBooks {
	return {
		...books,
		inventory: books.inventory ?? [],
		inventoryCategories: books.inventoryCategories ?? [],
		inventoryBrands: books.inventoryBrands ?? [],
	};
}

export function InventoryHub({
	books: rawBooks,
	columnPrefs = {},
}: {
	books: Books;
	columnPrefs?: Record<string, boolean>;
}) {
	const books = asInventoryBooks(rawBooks);
	const { docId: itemId } = useParams();
	const navigate = useNavigate();
	const client = useQueryClient();
	const session = useSession();
	const actor = session.data?.user.fullName || session.data?.user.email || "Someone";
	const searchRef = useRef<HTMLInputElement>(null);

	const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
	const [statusFilter, setStatusFilter] = useState("");
	const [custodyFilter, setCustodyFilter] = useState("");
	const [groupBy, setGroupBy] = useState<InventoryGroupBy>("");
	const [sortKey, setSortKey] = useState("astId");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [editing, setEditing] = useState<InventoryRow | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [columnVisibility, setColumnVisibility] = useState(() => mergeInventoryColumnVisibility(columnPrefs));

	useEffect(() => {
		setColumnVisibility(mergeInventoryColumnVisibility(columnPrefs));
	}, [columnPrefs]);

	useEffect(() => {
		if (searchOpen) searchRef.current?.focus();
	}, [searchOpen]);

	const rows = books.inventory;
	const detail = itemId ? inventoryRow(books, itemId) : undefined;

	const searchMatched = useMemo(() => {
		if (!searchQuery.trim()) return rows;
		return rows.filter((row) => inventoryRowMatchesSearch(row, searchQuery, vendorLabel(books, row.vendorId)));
	}, [books, rows, searchQuery]);

	const visibleRows = useMemo(() => {
		let list = searchMatched;
		if (statusFilter) list = list.filter((row) => row.status === statusFilter);
		if (custodyFilter) list = list.filter((row) => row.custodyTag === custodyFilter);
		return list;
	}, [searchMatched, statusFilter, custodyFilter]);

	const sorted = useMemo(() => {
		const copy = [...visibleRows];
		copy.sort((a, b) => {
			let av = "";
			let bv = "";
			if (sortKey === "vendorId") {
				av = vendorLabel(books, a.vendorId);
				bv = vendorLabel(books, b.vendorId);
			} else if (sortKey === "custody") {
				av = custodySummary(books, a);
				bv = custodySummary(books, b);
			} else {
				av = String(a[sortKey as keyof InventoryRow] ?? "");
				bv = String(b[sortKey as keyof InventoryRow] ?? "");
			}
			const order = av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" });
			return sortDir === "asc" ? order : -order;
		});
		return copy;
	}, [visibleRows, sortKey, sortDir, books]);

	async function persistColumnVisibility(next: Record<InventoryColumnId, boolean>) {
		setColumnVisibility(next);
		await api.updateHubInventoryColumnPrefs({ columns: next });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}

	function toggleColumn(columnId: InventoryColumnId, checked: boolean) {
		const next = { ...columnVisibility, [columnId]: checked };
		const visibleCount = Object.values(next).filter(Boolean).length;
		if (visibleCount === 0) return;
		void persistColumnVisibility(next);
	}

	function toggleSort(key: string) {
		if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
		else {
			setSortKey(key);
			setSortDir("asc");
		}
	}

	function pushHistory(
		base: InventoryHistoryEntry[],
		entry: Omit<InventoryHistoryEntry, "at" | "by">,
	): InventoryHistoryEntry[] {
		return [{ ...entry, at: new Date().toISOString(), by: actor }, ...base].slice(0, 50);
	}

	const refreshBooks = async () => {
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	};

	const form = useHubSave(async (fields) => {
		const category = textOf(fields, "category");
		const brand = textOf(fields, "brand");
		await Promise.all([
			ensureLookup("inventoryCategories", category, books),
			brand ? ensureLookup("inventoryBrands", brand, books) : Promise.resolve(),
		]);

		if (editing?.id) {
			const prior = editing;
			const next = inventoryFromForm(fields, prior.astId, prior.history ?? []);
			const history = [...(prior.history ?? [])];
			if (prior.status !== next.status) {
				history.unshift({
					at: new Date().toISOString(),
					by: actor,
					action: "status",
					detail: `Status changed from ${prior.status} to ${next.status}.`,
					from: prior.status,
					to: next.status,
				});
			}
			if (prior.custodyTag !== next.custodyTag || prior.customerId !== next.customerId || prior.siteId !== next.siteId) {
				history.unshift({
					at: new Date().toISOString(),
					by: actor,
					action: "custody",
					detail: `Custody updated to ${next.custodyTag === "external" ? custodySummary(books, next) : "On Hands"}.`,
					from: prior.custodyTag,
					to: next.custodyTag,
				});
			}
			await api.updateHubBook({ list: "inventory", id: editing.id, record: { ...next, history: history.slice(0, 50) } });
		} else {
			const astId = nextInventoryAstId(books.inventory);
			const record = inventoryFromForm(
				fields,
				astId,
				pushHistory([], { action: "create", detail: `Created ${astId}.` }),
			);
			const created = await api.appendHubBook({ list: "inventory", record });
			if (created.id) navigate(`/hub/inventory/${created.id}`);
		}
		setEditing(null);
	});

	const paginationResetKey = `${searchQuery}|${statusFilter}|${custodyFilter}|${groupBy}|${sortKey}|${sortDir}`;
	const pagination = useHubOperationPagination(sorted.length, paginationResetKey);
	const pagedSorted = useMemo(() => pagination.sliceRows(sorted), [pagination.sliceRows, sorted]);
	const groupedRows = useMemo(() => {
		if (!groupBy) return [["", pagedSorted] as [string, InventoryRow[]]];
		const map = new Map<string, InventoryRow[]>();
		for (const row of pagedSorted) {
			const key = inventoryGroupKey(row, books, groupBy);
			const list = map.get(key) ?? [];
			list.push(row);
			map.set(key, list);
		}
		return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
	}, [pagedSorted, groupBy, books]);

	const statusCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of searchMatched) counts.set(row.status, (counts.get(row.status) ?? 0) + 1);
		return counts;
	}, [searchMatched]);

	const custodyCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of searchMatched) counts.set(row.custodyTag, (counts.get(row.custodyTag) ?? 0) + 1);
		return counts;
	}, [searchMatched]);

	const listFiltered = Boolean(statusFilter || custodyFilter);

	async function remove(id: number) {
		if (!window.confirm("Remove this asset from inventory?")) return;
		await api.deleteHubBook({ list: "inventory", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		if (itemId === String(id)) navigate("/hub/inventory");
	}

	if (itemId && detail) {
		return <InventoryDetailView books={books} detail={detail} onRemove={(id) => void remove(id)} />;
	}

	if (itemId && !detail) {
		return (
			<div>
				<HubOperationEmpty>That asset was not found.</HubOperationEmpty>
				<Link className="btn ghost" to="/hub/inventory">All inventory</Link>
			</div>
		);
	}

	const searchActive = searchQuery.trim().length > 0;
	const filtered = searchActive || listFiltered;

	function clearFilters() {
		setStatusFilter("");
		setCustodyFilter("");
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${sorted.length} ${sorted.length === 1 ? "asset" : "assets"}${filtered ? " · filtered" : ""}`}>
				<HubOperationToolbar
					searchSlot={(
						<HubOperationSearchField
							id="hub-inventory-search"
							ariaLabel="Search inventory"
							placeholder="Search inventory…"
							searchOpen={searchOpen}
							searchQuery={searchQuery}
							searchRef={searchRef}
							onQueryChange={setSearchQuery}
							onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
						/>
					)}
					tools={(
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={searchActive}
								ariaLabel="Search inventory"
								onToggle={() => {
									if (searchOpen) setSearchQuery("");
									setSearchOpen(!searchOpen);
								}}
							/>
							<HubOperationColumnMenu
								columns={INVENTORY_TABLE_COLUMNS}
								visibility={columnVisibility}
								onToggle={(id, checked) => toggleColumn(id, checked)}
							/>
						</>
					)}
					primaryAction={(
						<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New asset</button>
					)}
				/>
			</HubOperationBar>
			<div className="task-filters">
				<HubOperationFilterMenu
					label="Status"
					ariaLabel="Asset status"
					value={statusFilter}
					onPick={setStatusFilter}
					active={Boolean(statusFilter)}
					counted
					items={[
						["", "All", searchMatched.length],
						...INVENTORY_STATUSES.map(([id, text]) => [id, text, statusCounts.get(id) ?? 0] as [string, string, number]),
					]}
				/>
				<HubOperationFilterMenu
					label="Custody"
					ariaLabel="Asset custody"
					value={custodyFilter}
					onPick={setCustodyFilter}
					active={Boolean(custodyFilter)}
					counted
					items={[
						["", "All", searchMatched.length],
						...INVENTORY_CUSTODY_TAGS.map(([id, text]) => [id, text, custodyCounts.get(id) ?? 0] as [string, string, number]),
					]}
				/>
				<HubOperationFilterMenu
					label="Group"
					ariaLabel="Group list"
					value={groupBy}
					onPick={(next) => {
						setGroupBy(next as InventoryGroupBy);
						setCollapsedGroups([]);
					}}
					active={Boolean(groupBy)}
					items={[
						["", "All"],
						["category", "Category"],
						["status", "Status"],
						["custody", "Custody"],
						["brand", "Brand"],
					]}
				/>
				{listFiltered ? <HubOperationFilterClear onClear={clearFilters} /> : null}
			</div>
			<HubOperationListTable>
				<InventoryGroupedTable
					books={books}
					groupedRows={groupedRows}
					collapsedGroups={collapsedGroups}
					onToggleGroup={(group) => setCollapsedGroups((current) => (
						current.includes(group) ? current.filter((c) => c !== group) : [...current, group]
					))}
					showGroupHeaders={Boolean(groupBy)}
					sortKey={sortKey}
					sortDir={sortDir}
					onSort={toggleSort}
					columnVisibility={columnVisibility}
					onRemove={(id) => void remove(id)}
					emptyMessage={filtered || rows.length > 0 ? "No assets match your filters." : "No inventory yet."}
				/>
				<HubOperationTablePagination
					total={sorted.length}
					page={pagination.page}
					pageSize={pagination.pageSize}
					totalPages={pagination.totalPages}
					onPage={pagination.setPage}
					onPageSize={pagination.setPageSize}
					totalNoun="assets"
				/>
			</HubOperationListTable>
			<HubOperationModal hubForm open={form.open} title="New asset" error={form.error} pending={form.pending} onClose={() => form.setOpen(false)} onSubmit={(fields) => void form.save(fields)}>
				<InventoryFormFields books={books} prefix="inv-new" editing={editing} onRefresh={refreshBooks} />
			</HubOperationModal>
		</HubOperationScreen>
	);
}
